import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
    Container,
    Row,
    Col,
    Card,
    CardHeader,
    CardBody,
    Table,
    Button,
    ButtonGroup,
    Spinner,
    Pagination,
    PaginationItem,
    PaginationLink,
    Modal,
    ModalHeader,
    ModalBody
} from 'reactstrap';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import { apiURL1 } from '../../config';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import './StudentResultPage.css';
import Footer from "components/Footers/Footer";


const baseURL = apiURL1;

function TeacherSalaryDetails() {
    const token = localStorage.getItem("token");

    const [salaries, setSalaries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [teacher, setTeacher] = useState(null);
    const [teacherId, setTeacherId] = useState(null);
    const [viewModalOpen, setViewModalOpen] = useState(false);
    const [selectedSalary, setSelectedSalary] = useState(null);
    const [madrasaInfo, setMadrasaInfo] = useState({});


    useEffect(() => {
        // Retrieve user info from localStorage
        const user = localStorage.getItem('userInfo');
        if (user) {
            try {
                const parsedUser = JSON.parse(user);
                setTeacherId(parsedUser._id);
            } catch (error) {
                console.error('Error parsing user info from localStorage:', error);
                toast.error('Error retrieving Staff ID.');
            }
        } else {
            toast.error('User info not found in localStorage.');
        }
    }, []);

    useEffect(() => {
        fetchMadrasaInfo();
    }, []);

    useEffect(() => {
        if (teacherId) {
            fetchSalaries();
        }
    }, [teacherId, page]);

    const fetchMadrasaInfo = async () => {
        try {
            const response = await axios.get(`${baseURL}/teacher/school-settings`, {
                headers: {
                    'Authorization': `Bearer ${token}`, // Include the token in the headers
                }
            });
            const data = response.data;
            if (data && data.length > 0) {
                setMadrasaInfo(data[0]);
            }
        } catch (error) {
            console.error('Error fetching madrasa info:', error);
        }
    };

    const fetchSalaries = async () => {
        if (!teacherId) {
            return;
        }

        setLoading(true);
        try {
            const response = await axios.get(
                `${baseURL}/teacher/getSalaryByPerson?teacherId=${teacherId}&page=${page}`
                , {
                    headers: {
                        'Authorization': `Bearer ${token}`, // Include the token in the headers
                    }
                }
            );
            if (response.data.salaries && response.data.salaries.length > 0) {
                setSalaries(response.data.salaries);
                setTotalPages(response.data.totalPages);
                setTeacher(response.data.salaries[0].teacherId);
            } else {
                setSalaries([]);
                setTotalPages(0);
                setTeacher(null);
                toast.info('No salary records found for this teacher.');
            }
        } catch (error) {
            console.error('Error fetching salaries:', error);
            toast.error('Error fetching salaries');
        } finally {
            setLoading(false);
        }
    };

    const renderPagination = () => {
        const paginationItems = [];
        for (let i = 1; i <= totalPages; i++) {
            paginationItems.push(
                <PaginationItem key={i} active={i === page}>
                    <PaginationLink onClick={() => setPage(i)}>{i}</PaginationLink>
                </PaginationItem>
            );
        }

        return (
            <Pagination style={{ justifyContent: 'center' }}>
                <PaginationItem disabled={page === 1}>
                    <PaginationLink previous onClick={() => setPage(page - 1)} />
                </PaginationItem>
                {paginationItems}
                <PaginationItem disabled={page === totalPages}>
                    <PaginationLink next onClick={() => setPage(page + 1)} />
                </PaginationItem>
            </Pagination>
        );
    };

    const handleView = (salary) => {
        setSelectedSalary(salary);
        setViewModalOpen(true);
    };

    const handleCloseViewModal = () => {
        setViewModalOpen(false);
        setSelectedSalary(null);
    };

    const generateSalarySlip = (salary) => {
        const pdf = new jsPDF();
        const pageWidth = pdf.internal.pageSize.getWidth();

        // School Logo and Information (Header)
        const img = new Image();
        img.src = "https://res.cloudinary.com/djg7v9dna/image/upload/v1742946371/mi0wavjsehps20kdzaet.png";

        pdf.addImage(img, 'PNG', 15, 15, 30, 30);

        pdf.setFontSize(16);
        pdf.setFont('helvetica', 'bold');
        pdf.text(madrasaInfo.schoolName || 'Madrasa Name', 50, 22);
        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'normal');
        pdf.text(madrasaInfo.schoolAddress || 'Madrasa Address', 50, 28);
        pdf.text(`Tel: ${madrasaInfo.schoolPhoneNumber || 'Phone'} | Email: ${madrasaInfo.schoolEmail || 'Email'}`, 50, 34);

        // Salary Slip Title
        pdf.setFontSize(18);
        pdf.text(`Salary Slip - ${teacher.name}`, pageWidth / 2, 55, { align: 'center' });

        let yPos = 70;
        pdf.setFontSize(12);
        pdf.text(`Staff Name: ${teacher.name}`, 15, yPos);
        pdf.text(`Email: ${teacher.email}`, pageWidth / 2 + 10, yPos);

        yPos += 10;
        pdf.text(`Month: ${dayjs().month((salary.month || 1) - 1).format('MMMM')}`, 15, yPos);
        pdf.text(`Year: ${salary.year}`, pageWidth / 2 + 10, yPos);

        // Salary Details Table
        yPos += 20;
        pdf.setFontSize(14);
        pdf.text('Salary Details', 15, yPos);

        yPos += 10;
        pdf.setFontSize(12);
        pdf.text(`Basic Salary: ${salary.basicSalary?.toFixed(2)}`, 15, yPos);
        yPos += 7;
        pdf.text(`Total Allowances: ${salary.totalAllowances?.toFixed(2)}`, 15, yPos);
        yPos += 7;
        pdf.text(`Total Deductions: ${salary.totalDeductions?.toFixed(2)}`, 15, yPos);
        yPos += 7;
        pdf.text(`Total Bonuses: ${salary.totalBonuses?.toFixed(2)}`, 15, yPos);
        yPos += 7;
        pdf.setFontSize(13);
        pdf.setFont('helvetica', 'bold');
        pdf.text(`Final Salary: ${salary.finalSalary?.toFixed(2)}`, 15, yPos);
        yPos += 10;

        // Function to add tables for allowances, deductions, and bonuses
        const addTable = (title, items, yPos) => {
            pdf.setFontSize(14);
            pdf.text(title, 15, yPos);

            yPos += 10;
            if (items && items.length > 0) {
                const tableData = items.map(item => [item.name, item.value]);
                pdf.autoTable({
                    head: [['Name', 'Value']],
                    body: tableData,
                    startY: yPos,
                    margin: { left: 15, right: 15 },
                });
                return pdf.autoTable.previous.finalY + 10;
            } else {
                pdf.setFontSize(12);
                pdf.text('No data available', 15, yPos);
                return yPos + 10;
            }
        };

        // Add tables for allowances, deductions, and bonuses
        yPos = addTable('Allowances', salary.allowances, yPos);
        yPos = addTable('Deductions', salary.deductions, yPos);
        yPos = addTable('Bonuses', salary.bonuses, yPos);

        // Footer
        const totalPages = pdf.getNumberOfPages();
        for (let i = 1; i <= totalPages; i++) {
            pdf.setPage(i);
            pdf.setFontSize(8);
            pdf.setTextColor(0, 0, 0);
            pdf.text(`Generated on: ${new Date().toLocaleDateString()} | ${madrasaInfo.schoolName || 'Madrasa Name'}`,
                pageWidth - 40, pdf.internal.pageSize.getHeight() - 10);
        }

        pdf.save(`Salary-Slip-${teacher.name}-${dayjs().month((salary.month || 1) - 1).format('MMMM')}-${salary.year}.pdf`);
    };

    const handlePrint = (salary) => {
        const tableStyle = `border-collapse: collapse; width: 100%; margin: 10px 0;`;
        const thStyle = `background-color: #2980b9; color: white; padding: 8px; text-align: left; border: 1px solid #ddd;`;
        const tdStyle = `padding: 8px; text-align: left; border: 1px solid #ddd;`;

        let printContent = `
            <html>
            <head>
                <title>Salary Slip - ${teacher.name}</title>
                <style>
                    body { font-family: Arial, sans-serif; margin: 20px; }
                    .header { text-align: center; margin-bottom: 20px; }
                    .school-name { font-size: 24px; font-weight: bold; }
                    .school-info { font-size: 14px; }
                    .salary-title { font-size: 20px; margin-bottom: 10px; }
                    table { ${tableStyle} }
                    th { ${thStyle} }
                    td { ${tdStyle} }
                    .footer { text-align: center; margin-top: 20px; font-size: 12px; }
                </style>
            </head>
            <body>
                <div class="header">
                    <img src="https://res.cloudinary.com/djg7v9dna/image/upload/v1739198257/Logo/kgamz0yjfr9uy8dsbnur.webp" alt="Madrasa Logo" width="100"/>
                    <div class="school-name">${madrasaInfo.schoolName || 'Madrasa Name'}</div>
                    <div class="school-info">
                        ${madrasaInfo.schoolAddress || 'Madrasa Address'}<br/>
                        Tel: ${madrasaInfo.schoolPhoneNumber || 'Phone'} | Email: ${madrasaInfo.schoolEmail || 'Email'}
                    </div>
                </div>
                <div class="salary-title">Salary Slip - ${teacher.name}</div>
                <div><strong>Month:</strong> ${dayjs().month((salary.month || 1) - 1).format('MMMM')}</div>
                <div><strong>Year:</strong> ${salary.year}</div>
                <div><strong>Basic Salary:</strong> ${salary.basicSalary?.toFixed(2)}</div>
                <div><strong>Total Allowances:</strong> ${salary.totalAllowances?.toFixed(2)}</div>
                <div><strong>Total Deductions:</strong> ${salary.totalDeductions?.toFixed(2)}</div>
                <div><strong>Total Bonuses:</strong> ${salary.totalBonuses?.toFixed(2)}</div>
                <div><strong>Final Salary:</strong> ${salary.finalSalary?.toFixed(2)}</div>
                
                ${salary.allowances && salary.allowances.length > 0 ? `
                    <div><strong>Allowances:</strong></div>
                    <table border="1">
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Value</th>
                            </tr>
                        </thead>
                        <tbody>
                            ${salary.allowances.map(allowance => `<tr><td style="${tdStyle}">${allowance.name}</td><td style="${tdStyle}">${allowance.value?.toFixed(2)}</td></tr>`).join('')}
                        </tbody>
                    </table>
                ` : ''}
                
                ${salary.deductions && salary.deductions.length > 0 ? `
                    <div><strong>Deductions:</strong></div>
                    <table border="1">
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Value</th>
                            </tr>
                        </thead>
                        <tbody>
                            ${salary.deductions.map(deduction => `<tr><td style="${tdStyle}">${deduction.name}</td><td style="${tdStyle}">${deduction.value?.toFixed(2)}</td></tr>`).join('')}
                        </tbody>
                    </table>
                ` : ''}
                
                ${salary.bonuses && salary.bonuses.length > 0 ? `
                    <div><strong>Bonuses:</strong></div>
                    <table border="1">
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Value</th>
                            </tr>
                        </thead>
                        <tbody>
                            ${salary.bonuses.map(bonus => `<tr><td>${bonus.name}</td><td>${bonus.value?.toFixed(2)}</td></tr>`).join('')}
                        </tbody>
                    </table>
                ` : ''}
                <div class="footer">Generated on: ${new Date().toLocaleDateString()}</div>
            </body>
            </html>
        `;

        const printWindow = window.open('', '', 'width=800,height=600');
        printWindow.document.write(printContent);
        printWindow.document.close();
        printWindow.print();
    };

    return (
        <Container>
            <Row>
                <Col>
                    <Card>
                        <CardHeader>
                            <h3>Salary Details</h3>
                        </CardHeader>
                        <CardBody>
                            {loading ? (
                                <div style={{ display: 'flex', justifyContent: 'center' }}>
                                    <Spinner color="primary" />
                                </div>
                            ) : (
                                <>
                                    {teacher ? (
                                        <>
                                            <h4>{teacher.name}</h4>
                                            <p><strong>Email:</strong> {teacher.email}</p>
                                            {/* <p><strong>Type:</strong> {teacher.type}</p> */}

                                            <Table>
                                                <thead>
                                                    <tr>
                                                        <th>Month</th>
                                                        <th>Year</th>
                                                        <th>Final Salary</th>
                                                        <th>Actions</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {salaries.length > 0 ? (
                                                        salaries.map((salary) => (
                                                            <tr key={salary._id}>
                                                                <td>{dayjs().month((salary.month || 1) - 1).format('MMMM')}</td>
                                                                <td>{salary.year}</td>
                                                                <td>{salary.finalSalary?.toFixed(2)}</td>
                                                                <td>
                                                                    <ButtonGroup size="sm">
                                                                        <Button color="info" size="sm" onClick={() => handleView(salary)}>
                                                                            View
                                                                        </Button>
                                                                        <Button color="primary" size="sm" onClick={() => generateSalarySlip(salary)}>
                                                                            Download
                                                                        </Button>
                                                                        <Button color="secondary" size="sm" onClick={() => handlePrint(salary)}>
                                                                            Print
                                                                        </Button>
                                                                    </ButtonGroup>
                                                                </td>
                                                            </tr>
                                                        ))
                                                    ) : (
                                                        <tr>
                                                            <td colSpan="4">No salary records found for this teacher.</td>
                                                        </tr>
                                                    )}
                                                </tbody>
                                            </Table>
                                            {renderPagination()}
                                        </>
                                    ) : (
                                        <p>No salary records found for this teacher.</p>
                                    )}
                                </>
                            )}
                        </CardBody>
                    </Card>
                </Col>
            </Row>

            {/* View Modal */}
            <Modal isOpen={viewModalOpen} toggle={handleCloseViewModal} size="lg">
                <ModalHeader toggle={handleCloseViewModal}>Salary Details</ModalHeader>
                <ModalBody>
                    {selectedSalary && (
                        <div>
                            <h4>{teacher.name}</h4>
                            <p><strong>Email:</strong> {teacher.email}</p>
                            <p><strong>Month:</strong> {dayjs().month((selectedSalary.month || 1) - 1).format('MMMM')}</p>
                            <p><strong>Year:</strong> {selectedSalary.year}</p>
                            <p><strong>Final Salary:</strong> {selectedSalary.finalSalary?.toFixed(2)}</p>

                            <p><strong>Basic Salary:</strong> {selectedSalary.basicSalary?.toFixed(2)}</p>
                            <p><strong>Total Allowances:</strong> {selectedSalary.totalAllowances?.toFixed(2)}</p>
                            <p><strong>Total Deductions:</strong> {selectedSalary.totalDeductions?.toFixed(2)}</p>
                            <p><strong>Total Bonuses:</strong> {selectedSalary.totalBonuses?.toFixed(2)}</p>

                            {/* Allowances */}
                            {selectedSalary.allowances && selectedSalary.allowances.length > 0 && (
                                <>
                                    <p><strong>Allowances:</strong></p>
                                    <ul>
                                        {selectedSalary.allowances.map(allowance => (
                                            <li key={allowance._id}>
                                                {allowance.name}: {allowance.value?.toFixed(2)}
                                            </li>
                                        ))}
                                    </ul>
                                </>
                            )}

                            {/* Deductions */}
                            {selectedSalary.deductions && selectedSalary.deductions.length > 0 && (
                                <>
                                    <p><strong>Deductions:</strong></p>
                                    <ul>
                                        {selectedSalary.deductions.map(deduction => (
                                            <li key={deduction._id}>
                                                {deduction.name}: {deduction.value?.toFixed(2)}
                                            </li>
                                        ))}
                                    </ul>
                                </>
                            )}

                            {/* Bonuses */}
                            {selectedSalary.bonuses && selectedSalary.bonuses.length > 0 && (
                                <>
                                    <p><strong>Bonuses:</strong></p>
                                    <ul>
                                        {selectedSalary.bonuses.map(bonus => (
                                            <li key={bonus._id}>
                                                {bonus.name}: {bonus.value?.toFixed(2)}
                                            </li>
                                        ))}
                                    </ul>
                                </>
                            )}

                            {/* Total Overtime */}
                            <p>
                                <strong>Total Overtime:</strong>
                            </p>
                            <p>
                                <strong>Hours:</strong> {selectedSalary.totalOvertime?.hours?.toFixed(2) || '0.00'}
                            </p>
                            <p>
                                <strong>Price Per Hour:</strong> {selectedSalary.totalOvertime?.pricePerHour?.toFixed(2) || '0.00'}
                            </p>
                            <p>
                                <strong>Total Overtime Amount:</strong> {selectedSalary.totalOvertime?.totalOvertimeAmount?.toFixed(2) || '0.00'}
                            </p>

                            {/* Attendance Details */}
                            <p>
                                <strong>Attendance Details:</strong>
                            </p>
                            {selectedSalary.attendanceDetails && (
                                <>
                                    {/* Full Days */}
                                    {selectedSalary.attendanceDetails.fullDays && (
                                        <>
                                            <p>
                                                <strong>Full Days:</strong>
                                            </p>
                                            <p>
                                                <strong>Count:</strong> {selectedSalary.attendanceDetails.fullDays.count?.toFixed(2)}
                                            </p>
                                            <p>
                                                <strong>Salary Per Day:</strong> {selectedSalary.attendanceDetails.fullDays.salaryPerDay?.toFixed(2)}
                                            </p>
                                            <p>
                                                <strong>Total Full Day Salary:</strong> {selectedSalary.attendanceDetails.fullDays.totalFullDaySalary?.toFixed(2)}
                                            </p>
                                        </>
                                    )}

                                    {/* Half Days */}
                                    {selectedSalary.attendanceDetails.halfDays && (
                                        <>
                                            <p>
                                                <strong>Half Days:</strong>
                                            </p>
                                            <p>
                                                <strong>Count:</strong> {selectedSalary.attendanceDetails.halfDays.count?.toFixed(2)}
                                            </p>
                                            <p>
                                                <strong>Salary Per Half Day (if any):</strong> {selectedSalary.attendanceDetails.halfDays.salaryPerHalfDay?.toFixed(2)}
                                            </p>
                                            <p>
                                                <strong>Total Half Day Salary:</strong> {selectedSalary.attendanceDetails.halfDays.totalHalfDaySalary?.toFixed(2)}
                                            </p>
                                        </>
                                    )}

                                    {/* Absences */}
                                    {selectedSalary.attendanceDetails.absences && (
                                        <>
                                            <p>
                                                <strong>Absences:</strong>
                                            </p>
                                            <p>
                                                <strong>Count:</strong> {selectedSalary.attendanceDetails.absences.count?.toFixed(2)}
                                            </p>
                                            <p>
                                                <strong>Deduction Per Absence (if any):</strong> {selectedSalary.attendanceDetails.absences.deductionPerAbsence?.toFixed(2)}
                                            </p>
                                            <p>
                                                <strong>Total Absence Deduction:</strong> {selectedSalary.attendanceDetails.absences.totalAbsenceDeduction?.toFixed(2)}
                                            </p>
                                        </>
                                    )}

                                    {/* Total Days (For Part Time) */}
                                    {selectedSalary.attendanceDetails.totalDays !== undefined && (
                                        <p>
                                            <strong>Total Days:</strong> {selectedSalary.attendanceDetails.totalDays?.toFixed(2)}
                                        </p>
                                    )}

                                    {/* Total Hours (For Visit) */}
                                    {selectedSalary.attendanceDetails.totalHours !== undefined && (
                                        <p>
                                            <strong>Total Hours:</strong> {selectedSalary.attendanceDetails.totalHours?.toFixed(2)}
                                        </p>
                                    )}
                                </>
                            )}
                        </div>
                    )}
                </ModalBody>
            </Modal>
            <Footer />
        </Container>
    );
}

export default TeacherSalaryDetails;
